import { useEffect, useRef, useState, type RefObject } from 'react';

const PTR_THRESHOLD_PX = 72;

export function usePullToRefresh({
  onRefresh,
  enabled,
  scrollRef,
}: {
  onRefresh: () => Promise<void> | void;
  enabled: boolean;
  scrollRef?: RefObject<HTMLElement | null>;
}): { refreshing: boolean } {
  const [refreshing, setRefreshing] = useState(false);
  const startY = useRef(0);
  const startX = useRef(0);
  const pulling = useRef(false);
  const onRefreshRef = useRef(onRefresh);
  onRefreshRef.current = onRefresh;

  useEffect(() => {
    if (!enabled) {
      return;
    }
    const target = scrollRef?.current;
    if (!target) {
      return;
    }

    const onStart = (event: TouchEvent) => {
      if (refreshing) {
        return;
      }
      const touch = event.touches[0];
      if (!touch || target.scrollTop > 0) {
        pulling.current = false;
        return;
      }
      pulling.current = true;
      startY.current = touch.clientY;
      startX.current = touch.clientX;
    };

    const onMove = (event: TouchEvent) => {
      if (!pulling.current || refreshing) {
        return;
      }
      const touch = event.touches[0];
      if (!touch) {
        return;
      }
      const deltaY = touch.clientY - startY.current;
      const deltaX = Math.abs(touch.clientX - startX.current);
      if (deltaX > deltaY) {
        pulling.current = false;
      }
    };

    const onEnd = async (event: TouchEvent) => {
      if (!pulling.current || refreshing) {
        pulling.current = false;
        return;
      }
      pulling.current = false;
      const touch = event.changedTouches[0];
      const deltaY = touch ? touch.clientY - startY.current : 0;
      const deltaX = touch ? Math.abs(touch.clientX - startX.current) : 0;
      if (deltaX >= deltaY || deltaY < PTR_THRESHOLD_PX) {
        return;
      }
      setRefreshing(true);
      try {
        await onRefreshRef.current();
      } finally {
        setRefreshing(false);
      }
    };

    target.addEventListener('touchstart', onStart, { passive: true });
    target.addEventListener('touchmove', onMove, { passive: true });
    target.addEventListener('touchend', onEnd);

    return () => {
      target.removeEventListener('touchstart', onStart);
      target.removeEventListener('touchmove', onMove);
      target.removeEventListener('touchend', onEnd);
    };
  }, [enabled, refreshing, scrollRef]);

  return { refreshing };
}
